/**
 * Badge and StatusBadge.
 *
 * Used by: order lists, the floor map, KDS tickets, the translation review
 * queue, device lists, audit severities — anywhere a state is shown.
 *
 * StatusBadge is the one every screen should reach for: it takes a RAW status
 * string from the API and resolves both the colour and the Vietnamese label
 * through `status.ts`. A screen that picks its own colour for "READY" is a bug,
 * because the same state would then look different on POS and on Admin.
 *
 * Accessibility: colour is never the only signal — the label is always
 * rendered, and the leading dot is decorative (`aria-hidden`).
 */
import type { ReactNode } from 'react';
import { cn } from '../utils.js';
import { statusLabelVi, TONE_CLASSES, toneFor, type Tone } from '../status.js';

export interface BadgeProps {
  readonly tone?: Tone;
  readonly variant?: 'soft' | 'solid';
  readonly size?: 'sm' | 'md';
  readonly dot?: boolean;
  readonly className?: string;
  readonly children: ReactNode;
}

export function Badge({
  tone = 'neutral',
  variant = 'soft',
  size = 'md',
  dot = false,
  className,
  children,
}: BadgeProps): React.JSX.Element {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full font-medium',
        size === 'sm' ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-1 text-xs',
        TONE_CLASSES[tone][variant],
        className,
      )}
    >
      {dot ? (
        <span
          aria-hidden="true"
          className={cn('h-1.5 w-1.5 rounded-full', variant === 'solid' ? 'bg-white/80' : TONE_CLASSES[tone].dot)}
        />
      ) : null}
      {children}
    </span>
  );
}

export interface StatusBadgeProps {
  /** The raw status code from the API, e.g. `NEEDS_CLEANING`. */
  readonly status: string | null | undefined;
  /** Override the Vietnamese label when a screen has better context. */
  readonly label?: string;
  readonly variant?: 'soft' | 'solid';
  readonly size?: 'sm' | 'md';
  readonly dot?: boolean;
  readonly className?: string;
}

export function StatusBadge({
  status,
  label,
  variant = 'soft',
  size = 'md',
  dot = true,
  className,
}: StatusBadgeProps): React.JSX.Element {
  return (
    <Badge tone={toneFor(status)} variant={variant} size={size} dot={dot} className={className}>
      {label ?? statusLabelVi(status)}
    </Badge>
  );
}
